import Auth from '@aws-amplify/auth';
//import axios from 'axios';

import {
  ENV,
  getStandardErrorObject,
  isStandardErrorObject,
  logError,
  logWarn,
  getTransactionId,
  normalizeCognitoGroups,
  urlHelper,
  getCookieValue,
  logInfo,
  getTokenBaseUrlByHostName,
  WINDOW_FEATURES_VALID_PROPERTIES,
  RESYNC_TOKENS_SUCCESS_SHOW_SIGN_IN_NO_REDIRECT,
  initiateInterceptor,
  getURLConfigs,
  getLoggingHeaders,
  SFLY3_TRK_COOKIE_NAME,
  SFLY3_TRK_COOKIE_VALUE,
  fetchWithTimeout,
} from '../Utils.js';

import { MyStorage } from '../MyStorage.js';
import { ClientErrors } from '../ClientErrors.js';
import { Events } from '../Events';
import { getCurrentEnv } from '@sfly/uig-current-environment-util/dist/core-js-3';
import Trackers from '../Trackers';
import {
  COOKIE_IFRAME_HTML,
  BRAND_OTP_SIGNIN_CAPTCHA_KEY,
} from '../Constants.js';
import {
  getCookiePreferencesApiCall,
  postPreferencesCookiesApiCall,
} from '../PreferenceCookiesStorage.js';
import { doGuestLoginApiCall, createGuestSession } from '../GuestLogin.js';
import { doBrandOtpSigninApiCall } from '../BrandOtpSignin.js';
import { addDataLayer } from '../datalayer.js';

const WAIT_TO_EXTEND_TOKENS = 10 * 60 * 1000; // 10 minutes

const EARLY_REFRESH_BUFFER_BY_ENV = {
  dev: 10 * 60 * 1000,
  stage: 10 * 60 * 1000,
  prod: 30 * 60 * 1000,
  production: 30 * 60 * 1000,
};
const EARLY_REFRESH_BUFFER = EARLY_REFRESH_BUFFER_BY_ENV[ENV] ?? 10 * 60 * 1000;
const POLL_COOKIE_EXISTENCE = 10 * 1000; // 10 seconds
const COGNIO_TOKENS_LAST_EXTENDED_AT = 'sflySsoLastExtended';

const IAM_LOCAL_STORAGE_CACHE_TIME = 10 * 60 * 1000; // 10 minutes
const IAM_LOCAL_STORAGE_CACHE_KEY = 'sflySsoIAMConfig';

const COOKIE_IFRAME_ID = 'sflySsoCookieFrame';

const BRAND_UID_KEYS = [
  'sfly_uid',
  'mlt_uid',
  'preschools_uid',
  'ltsmiles_uid',
];

export default class AuthClient {
  constructor() {
    this.currentUser = null;
    this.authTime = null;
    this.refreshToken = null;
    this.iamConfig = null;
    this.lastTriggered = 0; // Initialize throttle timestamp
    initiateInterceptor();
    // Handle the postMesage() from the cookie.html
    window.addEventListener(
      'message',
      (e) => {
        const { isTrusted, origin, data } = e;
        if (isTrusted && origin === getURLConfigs().BASE_URL_SSO) {
          if (this.currentUser !== null) {
            if (data === 'null') {
              // Clean up currentUser and other object upon cookie expiry
              this.currentUser = null;
              this.authTime = null;
              this.refreshToken = null;
              MyStorage.clear();
              document.dispatchEvent(new CustomEvent(Events.SESSION_LOGOUT));
            } else if (typeof data === 'string') {
              // USER-6291 Since we see messages other than ours on this we are checking the data a bit
              const decodedData = decodeURIComponent(data);
              if (
                decodedData.match(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g) !==
                  null &&
                this.currentUser.email !== decodedData
              ) {
                // When we see a mis-mismatch, normally customer service impersonation, reset the session
                // and resync tokens instead of reloading the page to avoid losing SPA state.
                const currentEmail = this.currentUser.email;
                const emailFromPostMessage = decodeURIComponent(data);
                const transId = getTransactionId();
                logError(
                  ClientErrors.CURRENT_USER_EMAIL_MISMATCH,
                  {
                    currentEmail,
                    emailFromPostMessage,
                  },
                  transId,
                );
                this.currentUser = null;
                this.refreshToken = null;
                this.resyncTokens().catch((resyncError) => {
                  logError(
                    ClientErrors.EMAIL_MISMATCH_RESYNC_TOKENS_FAILED,
                    {
                      currentEmail,
                      emailFromPostMessage,
                      resyncError,
                    },
                    transId,
                  );
                  window.location.reload();
                });
              }
            }
          } else if (typeof data === 'object') {
            if (
              data.eventId &&
              typeof data.eventId === 'string' &&
              data.eventId.match('^[a-zA-Z]+$')
            ) {
              if (
                data.eventId === Events.SIGN_IN_POPUP_FORM_SUBMITTED_SUCCESS
              ) {
                document.dispatchEvent(
                  new CustomEvent(Events.CLOSE_SIGN_IN_POPUP),
                );
              }
              if (
                data.eventId === Events.SIGN_IN_POPUP_FORM_SUBMITTED_FAILURE
              ) {
                const transId = getTransactionId();
                logError(
                  Events.SIGN_IN_POPUP_FORM_SUBMITTED_FAILURE,
                  null,
                  transId,
                );
              }
            }
          }
        }
      },
      true,
    );

    // Load the iFrame cookie.html that will do the window.postMessage()
    if (document.readyState === 'loading') {
      // Loading hasn't finished yet
      document.addEventListener('DOMContentLoaded', this.loadCookieIframe);
    } else {
      // `DOMContentLoaded` has already fired
      this.loadCookieIframe();
    }
  }

  loadCookieIframe() {
    var iframe = document.createElement('iframe');
    iframe.setAttribute(
      'style',
      'border: none; height: 0px; width: 0px; display: none;',
    );
    iframe.setAttribute('src', '');
    iframe.setAttribute('id', 'sflySsoCookieFrame');
    document.body.appendChild(iframe);
  }

  /**
   * Initialize and sync the Cognito in memory token store.
   * @returns {Promise<any>}
   */
  init() {
    // Track when the auth client is loaded
    logInfo('ZUUL_AUTH_CLIENT_LOADED');
    // addDataLayer will add DataLayer object to window
    addDataLayer();
    // if(ENV === 'production' || ENV === 'prod' || ENV === 'dev'){
    return new Promise((resolve) => {
      this.iamConfig = getURLConfigs().IAM_CONFIG_FALLBACK;
      this._configAuth();
      resolve();
    });
  }
  // init() {
  //   // Track when the auth client is loaded
  //   logInfo('ZUUL_AUTH_CLIENT_LOADED');

  //   if (ENV === 'production' || ENV === 'prod') {
  //     return new Promise((resolve) => {
  //       this.iamConfig = getURLConfigs().IAM_CONFIG_FALLBACK;
  //       this._configAuth();
  //       resolve();
  //     });
  //   } else {
  //     // Check local storage for cached IAM config and use that if it is still good.
  //     const cachedConfig = localStorage.getItem(IAM_LOCAL_STORAGE_CACHE_KEY);
  //     if (cachedConfig) {
  //       const tempConfig = JSON.parse(cachedConfig);
  //       if (
  //         tempConfig.lastCacheDate &&
  //         Date.now() - tempConfig.lastCacheDate < IAM_LOCAL_STORAGE_CACHE_TIME
  //       ) {
  //         this.iamConfig = tempConfig;
  //         this._configAuth();
  //         document.dispatchEvent(new CustomEvent(Events.AUTH_CLIENT_LOADED));
  //         return Promise.resolve();
  //       }
  //     }

  //     // Configure Auth
  //     const requestOptions = {
  //       method: 'GET',
  //       headers: { 'sfly-apikey': API_KEY_API2[ENV] },
  //       url: `${getURLConfigs().BASE_URL_API2}/user/iamconfig/v1/client/web`,
  //     };

  //     return new Promise((resolve) => {
  //       // axios(requestOptions)
  //       fetch(requestOptions.url, {
  //         headers: requestOptions.headers,
  //       })
  //         .then(async (response) => {
  //           const data = await response.json();
  //           // Store the config for later usage.
  //           this.iamConfig = Object.assign({ lastCacheDate: Date.now() }, data);
  //           // Cache the config for faster page reloads and less hits to the server
  //           localStorage.setItem(
  //             IAM_LOCAL_STORAGE_CACHE_KEY,
  //             JSON.stringify(this.iamConfig),
  //           );
  //           this._configAuth();
  //         })
  //         .catch((e) => {
  //           // Log an error for tracking purposes and tracking fail rates on this endpoint
  //           const transId = getTransactionId();
  //           logError(ClientErrors.IAM_SYNC, e, transId);
  //           // Set the Auth config to the fallbacks we hardcode in the Utils.js
  //           this.iamConfig = getURLConfigs().IAM_CONFIG_FALLBACK;
  //           this._configAuth();
  //         })
  //         .finally(() => {
  //           // We resolve not matter what since we fallback to acceptable defaults if things go bad with this endpoint.
  //           document.dispatchEvent(new CustomEvent(Events.AUTH_CLIENT_LOADED));
  //           resolve();
  //         });
  //     });
  //   }
  // }

  /**
   * Get the current user information
   * @returns {object}
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Return the prospective status of this user or browser session
   * @returns {object}
   */
  getProspectInformation() {
    const dataCookie = getCookieValue(
      getCurrentEnv() === 'production'
        ? 'sflyData2'
        : `sflyData2_${getCurrentEnv()}`,
    );
    const visitorId = getCookieValue(
      getCurrentEnv() === 'production'
        ? 'visitor'
        : `visitor_${getCurrentEnv()}`,
    );
    const isProspect = this.currentUser === null && dataCookie === null;
    return {
      isProspect: isProspect,
      visitorId: isProspect || this.currentUser === null ? visitorId : '',
    };
  }

  /**
   * Returns a boolean based on the age of the tokens and if the user will require elevation.
   * If forceElevation is enabled it should always return true for all preprod environments.
   * @returns {boolean}
   */
  isElevationNeeded() {
    let currentUser =
      typeof this.currentUser == 'undefined' ? null : this.currentUser;
    if (currentUser && currentUser.user_type === 'GUEST') {
      return true;
    }
    if (
      getCookieValue('forceElevation') === 'true' &&
      ENV !== 'production' &&
      ENV !== 'prod'
    ) {
      return true;
    } else if (
      this.authTime &&
      this.iamConfig.elevationEnabled &&
      Date.now() - this.authTime * 1000 >
        this.iamConfig.elevationDuration * 1000
    ) {
      logInfo({
        logName: 'ELEVATION_NEEDED',
        isElevationNeeded: true,
        email: window.$sflyAuth?.currentUser.email,
        uid: window.$sflyAuth?.currentUser.sfly_uid,
        elevationDuration: this.iamConfig.elevationDuration,
        authTime: this.iamConfig.authTime,
        currentTime: Date.now(),
        pageUrl: window.location.href,
      });
      return true;
    }
    return false;
  }

  /**
   * Show the elevate sign in view at the SSO server. Does not redirect if user is already logged in
   *
   * @param configObject {
   *     redirectUri
   *     brand
   *     source
   * }
   *
   */
  async requireElevation(configObject) {
    let idTokenPromiseResult = await this.getIdToken();
    if (idTokenPromiseResult.payload.user_type === 'GUEST') {
      window.location.assign(
        urlHelper(getURLConfigs().BASE_URL_SSO + '/', configObject),
      );
    } else {
      window.location.assign(
        urlHelper(getURLConfigs().BASE_URL_SSO + '/elevate', configObject),
      );
    }
  }

  /**
   * Show the that the logged in account is guest user or not
   *
   */

  // async isGuestAccount() {
  //     let currentUser = typeof (this.currentUser) == 'undefined' ? null : this.currentUser;
  //     if (currentUser) {
  //         if (currentUser.user_type === 'GUEST') {
  //             return true;
  //         }
  //         else
  //             return false;
  //     }
  //     else
  //         return false;
  // }
  /**
   *
   * @param configObject {
   *     redirectUri - The url the SSO server should redirect to after the action
   *     brand - SFLY, TP, LT, etc.
   *     source - The context message of the brand. Brands can have more than one login or signup context like "Sign in to make a book".
   *     email
   *     cid
   * }
   *
   */
  showSignIn(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/', configObject),
    );
  }

  showSignInNoRedirect(
    configObject,
    statusCallback,
    windowId = 'ssosignin',
    windowFeatures,
    redirectFromSsoClient,
  ) {
    let windowFeaturesConfig = '';
    const isMobile = window.matchMedia(
      'only screen and (max-width: 480px)',
    ).matches;
    if (windowFeatures && typeof windowFeatures === 'object') {
      windowFeaturesConfig = Object.entries(windowFeatures).reduce(
        (result, [key, value]) => {
          if (WINDOW_FEATURES_VALID_PROPERTIES.includes(key)) {
            if (isMobile) {
              if (key === 'width' || key === 'innerWidth')
                value = window.innerWidth;
              if (key === 'height' || key === 'innerHeight')
                value = window.innerHeight;
              if (
                key === 'screenX' ||
                key === 'left' ||
                key === 'screenY' ||
                key === 'top'
              )
                value = 0;
            }
            result += `${key}=${value},`;
          }
          return result;
        },
        '',
      );
    }
    if (windowFeaturesConfig === '') {
      const dualScreenLeft =
        window.screenLeft !== undefined ? window.screenLeft : window.screenX;
      const dualScreenTop =
        window.screenTop !== undefined ? window.screenTop : window.screenY;
      const openerWidth = window.innerWidth
        ? window.innerWidth
        : document.documentElement.clientWidth
          ? document.documentElement.clientWidth
          : window.screen.width;
      const openerHeight = window.innerHeight
        ? window.innerHeight
        : document.documentElement.clientHeight
          ? document.documentElement.clientHeight
          : window.screen.height;
      const systemZoom = openerWidth / window.screen.availWidth;
      const defaultWidth = isMobile ? window.innerWidth : 500;
      const defaultHeight = isMobile ? window.innerHeight : 800;
      const left =
        (openerWidth - defaultWidth) / 2 / systemZoom + dualScreenLeft;
      const top =
        (openerHeight - defaultHeight) / 2 / systemZoom + dualScreenTop;
      windowFeaturesConfig = `innerWidth=${defaultWidth},innerHeight=${defaultHeight},left=${left},top=${top}`;
    }

    const ssoUrl = urlHelper(getURLConfigs().BASE_URL_SSO + '/', {
      popup: true,
    });
    const signInPopUp = window.open('', windowId, windowFeaturesConfig);
    if (signInPopUp) {
      signInPopUp.location = ssoUrl;
      signInPopUp.focus();
    }

    const focusOpenerAndClose = () => {
      if (window.opener) window.focus();
      if (signInPopUp) signInPopUp.close();
    };

    const handleStatusCallback = (success, error) => {
      if (statusCallback && typeof statusCallback === 'function') {
        const payload = { success };
        if (error && error instanceof Error) payload.error = error;
        statusCallback(payload);
      }
    };

    const closeSignInPopUp = async () => {
      const transId = getTransactionId();
      let recursiveInitCall = 0;
      try {
        const results = await this._recursiveResyncTokens(recursiveInitCall);
        if (results) {
          logInfo(RESYNC_TOKENS_SUCCESS_SHOW_SIGN_IN_NO_REDIRECT);
          handleStatusCallback(true);
          focusOpenerAndClose();
        }
      } catch (error) {
        logError(
          ClientErrors.SIGN_NO_REDIRECT_RESYNC_TOKENS_FAILED,
          error,
          transId,
        );
        handleStatusCallback(false, error);
        if (redirectFromSsoClient && window.opener) {
          window.opener.location.assign(
            urlHelper(getURLConfigs().BASE_URL_SSO + '/', configObject),
          );
        }
        focusOpenerAndClose();
      }
      document.removeEventListener(
        Events.CLOSE_SIGN_IN_POPUP,
        closeSignInPopUp,
      );
    };

    document.addEventListener(Events.CLOSE_SIGN_IN_POPUP, closeSignInPopUp);
    return signInPopUp;
  }

  /**
   *
   * @param configObject {
   *     redirectUri - The url the SSO server should redirect to after the action
   *     brand - SFLY, TP, LT, etc.
   *     source - The context message of the brand. Brands can have more than one login or signup context like "Sign in to make a book".
   *     email
   *     givenName
   *     familyName
   *     contentSpace - Dynamic content well of the signup page
   *     cid - Campaign id for signup
   * }
   *
   */
  showSignUp(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/signup', configObject),
    );
  }

  /**
   *
   * @param configObject {
   *     redirectUri - The url the SSO server should redirect to after the action
   *     brand - SFLY, TP, LT, etc.
   *     source - The context message of the brand. Brands can have more than one login or signup context like "Sign in to make a book".
   *     email
   *     givenName
   *     familyName
   *     contentSpace - Dynamic content well of the signup page
   *     cid - Campaign id for signup
   * }
   *
   */
  showTilesHello(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/tilesHello', configObject),
    );
  }

  /**
   *
   * @param configObject {
   *     brand - SFLY, MLT, PRESTIGE, PRESCHOOLS
   *     redirectUri - The url the SSO server should redirect to after the action
   * }
   */
  showChangeProfile(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/profile', configObject),
    );
  }

  /**
   *
   * @param configObject {
   *     redirectUri - The url the SSO server should redirect to after any successful action
   *     brand - SFLY, TP, LT, etc.
   * }
   *
   */
  showCCPA(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/ccpa', configObject),
    );
  }

  /**
   * Redirect to the sso server for logout. Takes a redirect url to redirect to or redirects to the sso
   * sign in page after logout.
   *
   * @param configObject {
   *     redirectUri
   * }
   *
   */
  signout(configObject) {
    // Pass an empty string to the urlHelper to not trigger an auto redirectUri param.
    if (configObject && !configObject.redirectUri) {
      configObject.redirectUri = '';
    } else if (!configObject) {
      configObject = { redirectUri: '' };
    }
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/logout', configObject),
    );
  }

  /**
   * Navigates to the change password view on the sso server
   * @param configObject
   */
  changePassword(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/changepassword', configObject),
    );
  }

  /**
   * Send a user through the brand migration flow
   */
  migrateBrandAccount(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/migrate', configObject),
    );
  }

  /**
   * Send a user through the brand OTP sign in flow which migrates client
   * brands into Cognito via the migrate sign in page.
   */
  migrateBrandAccountSignIn(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/migratesignin', configObject),
    );
  }

  /**
   * Send a user through the brand OTP sign up flow which migrates client
   * brands into Cognito via the migrate sign up page.
   */
  migrateBrandAccountSignUp(configObject) {
    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/migratesignup', configObject),
    );
  }

  /**
   * Starts the brand OTP sign in flow by sending a one time code to the given
   * email (the programmatic equivalent of the `handleSendCode` step on the
   * migrate sign in page). On success the user is redirected to the migrate
   * sign in page with the `otpReferenceToken`, where the OTP entry view opens
   * so the user can fill in the code they received and finish signing in.
   *
   * The `recaptchaToken` and `siteKey` are generated by the caller and come in
   * on the `configObject`; `automationKey` is read from the cookie inside
   * `doBrandOtpSigninApiCall`. `recapthaImageValue` (and the `recapthaText` the
   * user typed) only come back from a previous low score error response and are
   * forwarded on the retry.
   *
   * @param {Object} configObject
   * @param {string} configObject.email - Email to send the code to. Required.
   * @param {string} configObject.brand - Brand the user is signing in to. Required.
   * @param {string} [configObject.recaptchaToken] - Recaptcha token generated by the caller.
   * @param {string} [configObject.siteKey] - Recaptcha site key provided by the caller.
   * @param {string} [configObject.recapthaText] - Challenge text the user typed for the low score image path.
   * @param {Object} [configObject.recapthaImageValue] - Image challenge data from a previous error response.
   * @returns {Promise<{ otpReferenceToken: string } | void>} Resolves with the
   *   otpReferenceToken on success; resolves with `void` on the reCAPTCHA low
   *   score path, where the user is redirected to solve the image challenge.
   */
  async brandOtpSignin(configObject = {}) {
    // recaptchaToken / siteKey / recapthaText / recapthaImageValue are only used
    // for the api call; keep them out of the redirect so they don't leak into
    // the query string.
    const {
      email,
      brand,
      recaptchaToken,
      siteKey,
      recapthaText,
      recapthaImageValue,
      ...redirectConfig
    } = configObject;

    // The brand OTP sign in flow is only for signed-out users. If we can get an
    // idToken the user is already logged in, so bail out instead of starting it.
    let idToken = null;
    try {
      idToken = await this.getIdToken();
    } catch {
      idToken = null;
    }
    if (idToken) {
      const transId = getTransactionId();
      logError(
        ClientErrors.BRAND_OTP_SIGNIN_ALREADY_LOGGED_IN,
        { email: this.currentUser?.email, brand },
        transId,
      );
      throw getStandardErrorObject(
        ClientErrors.BRAND_OTP_SIGNIN_ALREADY_LOGGED_IN,
        transId,
      );
    }

    let result;
    try {
      result = await doBrandOtpSigninApiCall({
        email,
        brand,
        recaptchaToken,
        siteKey,
        recapthaText,
        recapthaImageValue,
      });
    } catch (err) {
      // Recaptcha low score challenge: rather than surfacing the error, hand the
      // user off to the migrate sign in page so they can solve the image
      // challenge there. The captchaImage is too large for the query string, so
      // stash the challenge data in sessionStorage and redirect with a flag the
      // migrate sign in page reads on load.
      if (err?.captchaImage) {
        try {
          window.sessionStorage.setItem(
            BRAND_OTP_SIGNIN_CAPTCHA_KEY,
            JSON.stringify({
              captchaImage: err.captchaImage,
              assessmentId: err.assessmentId,
              tokenId: err.tokenId,
            }),
          );
        } catch {
          // sessionStorage can throw (private mode / quota); fall through to the
          // redirect so the user still lands on the migrate sign in page.
        }
        window.location.assign(
          urlHelper(getURLConfigs().BASE_URL_SSO + '/migratesignin', {
            ...redirectConfig,
            email: encodeURIComponent((email || '').trim().toLowerCase()),
            brand,
            captchaChallenge: 'true',
          }),
        );
        return;
      }
      throw err;
    }

    window.location.assign(
      urlHelper(getURLConfigs().BASE_URL_SSO + '/migratesignin', {
        ...redirectConfig,
        email: encodeURIComponent((email || '').trim().toLowerCase()),
        brand,
        otpReferenceToken: encodeURIComponent(result.otpReferenceToken),
      }),
    );
    return result;
  }

  /**
   * Purges the in memory storage the AuthClient uses to store the Cognito tokens and resyncs the tokens from
   * the SSO tokens endpoint. This is usefully for Single Page Apps that need to "re-check" the initial login
   * status after the SPA is loaded and a user logs in on another tab or from another brand site.
   * @param {boolean} [forceRefresh] Logged so we can tell a forced early refresh from a scheduled resync.
   * @returns {Promise<any>}
   */
  resyncTokens(forceRefresh) {
    // Drop the cached sync promise so sync() refetches; tokens are cleared on a 200.
    MyStorage.tokenSyncPromise = null;
    return new Promise((resolve, reject) => {
      MyStorage.sync()
        .then(() => {
          this.getIdTokenPromise = null;
          this.getIdToken(false, true)
            .then((t) => {
              logInfo({
                logName: 'FORCE_RESYNC_TOKENS_GET_ID_EARLY_REFRESH_SUCCESS',
                sfly_uid: this.currentUser?.sfly_uid,
                email: this.currentUser?.email,
                cup_id: this.currentUser?.cup_id,
                forceRefresh: forceRefresh,
              });

              resolve(t);
            })
            .catch((e) => {
              // Ending up logged out is expected (e.g. /sync returned 204), so
              // it is a warning; any other getIdToken failure is a real error.
              const isNotLoggedIn =
                e?.error?.splunkLabel ===
                ClientErrors.NOT_LOGGED_IN.splunkLabel;
              (isNotLoggedIn ? logWarn : logError)(
                {
                  ...(isNotLoggedIn
                    ? ClientErrors.FORCE_RESYNC_TOKENS_NOT_LOGGED_IN
                    : ClientErrors.FORCE_RESYNC_TOKENS_GET_ID_TOKEN_EXCEPTION),
                  forceRefresh: forceRefresh,
                },
                e,
              );

              reject(e);
            });
        })
        .catch((e) => {
          logError(
            {
              ...ClientErrors.FORCE_RESYNC_TOKENS_EXCEPTION,
              forceRefresh: forceRefresh,
            },
            e,
          );

          reject(e);
        });
    });
  }

  /**
   * Gets the current user information from the cognito sdk and returns a Promise.
   * @returns {Promise<any>}
   */
  getIdToken(verbose, skipResync = false) {
    // If we have an unresolved promise for this object return it.
    if (this.getIdTokenPromise) {
      return this.getIdTokenPromise;
    }

    window.resyncTokens = this.resyncTokens.bind(this);
    // Create a cache for the promise since calling this 15 times at once causes server calls
    this.getIdTokenPromise = new Promise((resolve, reject) => {
      initiateInterceptor();
      Auth.currentAuthenticatedUser()
        .then((e) => {
          const signInUserSession = e.signInUserSession;

          if (signInUserSession && signInUserSession.idToken) {
            const payload = signInUserSession.idToken.payload;
            if (payload) {
              if (this.currentUser === null) {
                this.currentUser = {
                  given_name: payload.given_name,
                  family_name: payload.family_name,
                  email: payload.email,
                  groups: normalizeCognitoGroups(payload['cognito:groups']),
                  socialProviders: normalizeCognitoGroups(
                    payload['cognito:groups'],
                    true,
                  ),
                  cup_id: payload['cognito:username'],
                  user_type: payload['user_type'],
                  impersonated_user: payload.impersonated_user,
                  ...(payload['guest_email'] && {
                    guest_email: payload['guest_email'],
                  }),
                };

                // Append the brand UIDs if they exist in the payload
                BRAND_UID_KEYS.forEach((k) => {
                  if (payload[k]) {
                    this.currentUser[k] = payload[k];
                  }
                });
              }
              // Set the last auth time from the payload for isElevated calculation
              this.authTime = payload.auth_time;

              // Set refresh token to give to the app server
              if (this.refreshToken === null) {
                this.refreshToken = signInUserSession.refreshToken.token;
                // Start loading the cookie iframe that tells if the user logs out.
                this._checkSsoCookies();
              }

              // Extend cookies on the SSO server
              if (MyStorage.getTokenData()['persistUserLogin'] !== 'on') {
                let lastExtended = localStorage.getItem(
                  COGNIO_TOKENS_LAST_EXTENDED_AT,
                );
                // The condition prevents calling extendTokens endpoint for subsequent getIdToken calls from client.
                if (
                  lastExtended !== undefined &&
                  new Date().getTime() - lastExtended > WAIT_TO_EXTEND_TOKENS
                ) {
                  this._extendCognitoCookies();
                }
                if (lastExtended === undefined) {
                  // Initially last extended should be when the app was loaded for first time.
                  localStorage.setItem(
                    COGNIO_TOKENS_LAST_EXTENDED_AT,
                    new Date().getTime(),
                  );
                }
              }

              // Force a refresh when token is expired or about to expire within 10 minutes.
              // Returning a Promise from .then() ensures .finally() waits for resyncTokens
              // to complete before clearing the getIdTokenPromise cache. resyncTokens'
              // outcome is routed to resolve/reject explicitly — without this, a successful
              // resync would never settle this call's promise and the caller would hang.
              const msUntilExpiry = payload.exp * 1000 - Date.now();
              if (msUntilExpiry < EARLY_REFRESH_BUFFER && !skipResync) {
                return this.resyncTokens(true).then(resolve, reject);
              }
            }

            // if verbose we will return the whole freaking thing. See getSignInUserSession()
            if (verbose) {
              resolve(signInUserSession);
            } else {
              // Otherwise we follow the 99% rule and return the only thing they need
              resolve(signInUserSession.idToken);
            }
          } else {
            // Get a transId to link errors from the client to splunk
            const transId = getTransactionId();
            logError(ClientErrors.INVALID_COGNITO_SESSION, null, transId);
            reject(
              getStandardErrorObject(
                ClientErrors.INVALID_COGNITO_SESSION,
                null,
                transId,
              ),
            );
          }
        })
        .catch((e) => {
          if (
            (typeof e === 'string' && e === 'The user is not authenticated') ||
            (isStandardErrorObject(e) &&
              e?.error?.splunkLabel === ClientErrors.TOKEN_SYNC.splunkLabel)
          ) {
            if (!skipResync) {
              // Before giving up, try to recover an existing session from the
              // SSO cookie (e.g. a guest session created in another tab).
              this.resyncTokens()
                .then((token) => resolve(token))
                .catch(() =>
                  reject(getStandardErrorObject(ClientErrors.NOT_LOGGED_IN)),
                );
            } else if (
              isStandardErrorObject(e) &&
              e?.error?.splunkLabel === ClientErrors.TOKEN_SYNC.splunkLabel
            ) {
              // Already in a resync and it's a TOKEN_SYNC failure specifically -
              // log it and preserve the original error instead of masking it
              // with a generic NOT_LOGGED_IN.
              const transId = getTransactionId();
              logError(
                {
                  ...ClientErrors.TOKEN_SYNC_GET_ID_TOKEN,
                  methodName: 'getIdToken',
                  skipResync: skipResync,
                },
                // e is the wrapper MyStorage rejects with, not the original fetch
                // failure - MyStorage already logged that failure's real name,
                // message, status and stack under TOKEN_SYNC. e.stack here is
                // just this call chain re-surfacing through getIdToken, so only
                // the original transId is worth carrying over, to join the two
                // events in Splunk.
                {
                  transId: e?.transId,
                  code: e?.error?.code,
                  message: e?.error?.splunkLabel,
                },
                transId,
              );
              reject(e);
            } else {
              // Already in a resync — truly not authenticated
              reject(getStandardErrorObject(ClientErrors.NOT_LOGGED_IN));
            }
          } else if (
            (typeof e === 'string' &&
              e.toLowerCase() === 'unauthorized token in degraded mode') ||
            (typeof e === 'object' &&
              typeof e.message === 'string' &&
              e.message.toLowerCase() ===
                'unauthorized token in degraded mode' &&
              (e.code === '13016' || e.code === 13016)) ||
            (isStandardErrorObject(e) &&
              typeof e.error === 'object' &&
              typeof e.error.message === 'string' &&
              e.error.message.toLowerCase() ===
                'unauthorized token in degraded mode' &&
              (e.error.code === '13016' || e.error.code === 13016))
          ) {
            const url = getURLConfigs().BASE_URLS[ENV].sso;
            this.signout({ redirectUri: url });
          } else {
            logError(ClientErrors.CURRENT_AUTH_USER_EXCEPTION, e);
            reject(
              getStandardErrorObject(ClientErrors.CURRENT_AUTH_USER_EXCEPTION),
            );
          }
        })
        .finally(() => {
          // sets data layers for tracking software
          try {
            Trackers(this);
          } catch (err) {
            const transId = getTransactionId();
            logError(ClientErrors.TRACKER_FAILED, null, transId);
          }

          // reset the promise cache
          this.getIdTokenPromise = null;
        });
    });

    // return the newly created getIdTokenPromise
    return this.getIdTokenPromise;
  }

  /**
   * Get the full cognito user session with idToken, accessToken, and refreshToken for clients like Make My Book.
   * @returns {Promise<any>}
   */
  getSignInUserSession() {
    return this.getIdToken(true);
  }

  getGuestIdToken(redirectUri) {
    if (this.getGuestIdTokenPromise) {
      return this.getGuestIdTokenPromise;
    }

    // If $sflyAuth is null then, call guest flow apis.
    this.getGuestIdTokenPromise = new Promise((resolve, reject) => {
      if (!redirectUri) {
        const transId = getTransactionId();
        logError(ClientErrors.GUEST_REDIRECT_URI_NOT_FOUND, transId);
        reject(
          getStandardErrorObject(
            ClientErrors.GUEST_REDIRECT_URI_NOT_FOUND,
            transId,
          ),
        );
        return;
      }

      this.getIdToken()
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          if (
            getCookieValue(
              'CognitoIdentityServiceProvider.' +
                this.iamConfig?.appClientId +
                '.LastAuthUser',
            )
          ) {
            const transId = getTransactionId();
            logError(
              {
                ...ClientErrors.TOKEN_SYNC_GET_ID_TOKEN,
                methodName: 'getGuestIdToken',
              },
              { transId: e?.transId, error: e?.error },
              transId,
            );
            if (e?.transId) {
              e.transId = transId;
            }
            reject(getStandardErrorObject(e.error, e?.transId ?? transId));
            return;
          }

          doGuestLoginApiCall(redirectUri)
            .then(() => {
              resolve();
            })
            .catch((e) => {
              reject(e);
            });
        });
    }).finally(() => {
      this.getGuestIdTokenPromise = null;
    });
    return this.getGuestIdTokenPromise;
  }

  /**
   * Same intent as getGuestIdToken(), but for callers that must not navigate
   * the page: creates the guest account via createGuestSession() (no bounce
   * form/redirect) and then picks up the resulting session in-page via
   * resyncTokens(), instead of a full-page POST to /v3/sso/brand/SFLY/sync.
   * @param {string} [redirectUri] optional; used as redirectUri when a
   * degraded-mode 401 sends the user to otpLogin (defaults to the current page).
   * @returns {Promise<any>}
   */
  fetchGuestSession(redirectUri) {
    if (this.fetchGuestSessionPromise) {
      return this.fetchGuestSessionPromise;
    }

    this.fetchGuestSessionPromise = new Promise((resolve, reject) => {
      this.getIdToken()
        .then((res) => {
          resolve(res);
        })
        .catch((e) => {
          if (
            getCookieValue(
              'CognitoIdentityServiceProvider.' +
                this.iamConfig?.appClientId +
                '.LastAuthUser',
            )
          ) {
            const transId = getTransactionId();
            logError(
              {
                ...ClientErrors.TOKEN_SYNC_GET_ID_TOKEN,
                methodName: 'fetchGuestSession',
              },
              { transId: e?.transId, error: e?.error },
              transId,
            );
            if (e?.transId) {
              e.transId = transId;
            }

            reject(getStandardErrorObject(e.error, e?.transId ?? transId));
            return;
          }
          createGuestSession(redirectUri)
            .then(() => {
              // The guest account was created successfully regardless of
              // whether resync succeeds, so a resync hiccup here resolves
              // rather than rejects.
              this.resyncTokens()
                .then((token) => resolve(token))
                .catch(() => resolve());
            })
            .catch((e) => {
              reject(e);
            });
        });
    }).finally(() => {
      this.fetchGuestSessionPromise = null;
    });
    return this.fetchGuestSessionPromise;
  }

  /**
   * Checks whether the current session belongs to a guest user and, if so,
   * redirects the browser to the Shutterfly login wall with `href` as the
   * post-login destination. If the user is already authenticated, resolves
   * with the idToken.
   *
   * @param {string} href - The URL to return the user to after login (typically window.location.href).
   * @returns {Promise<idToken>} Resolves with the idToken when the user is authenticated.
   */
  isLoginWallRequired(href) {
    return new Promise((resolve, reject) => {
      if (!href) {
        const transId = getTransactionId();
        logError(ClientErrors.GUEST_REDIRECT_URI_NOT_FOUND, transId);
        reject(
          getStandardErrorObject(
            ClientErrors.GUEST_REDIRECT_URI_NOT_FOUND,
            transId,
          ),
        );
        return;
      }

      this.getIdToken()
        .then((idToken) => {
          if (
            idToken &&
            idToken.payload &&
            idToken.payload['user_type'] === 'GUEST'
          ) {
            window.location.assign(
              getURLConfigs().BASE_URL_SSO +
                '/?redirectUri=' +
                encodeURIComponent(href),
            );
            // Promise intentionally left pending — redirect takes over the page.
          } else {
            resolve(idToken);
          }
        })
        .catch(() => {
          window.location.assign(
            getURLConfigs().BASE_URL_SSO +
              '/?redirectUri=' +
              encodeURIComponent(href),
          );
          // Promise intentionally left pending — redirect takes over the page.
        });
    });
  }

  /**
   * Helper method to configure the auth client once we set the object from the server or from the fall back configs
   * @private
   */
  _configAuth() {
    Auth.configure({
      userPoolId: this.iamConfig.userPoolId,
      userPoolWebClientId: this.iamConfig.appClientId,
      region: this.iamConfig.region,
      storage: MyStorage,
    });
    Auth.userPool.client.endpoint = this.iamConfig.endPointUrl;
  }

  /**
   * Calls the check endpoint to extend the cookie life on the app server for non persistent users.
   * @private
   */
  _extendCognitoCookies() {
    fetchWithTimeout(`${getTokenBaseUrlByHostName()}/v3/oauth2/sso/extend`, {
      credentials: 'include', // This is the equivalent of `withCredentials: true` in Axios
      headers: {
        ...getLoggingHeaders(),
      },
    })
      .then(() => {
        localStorage.setItem(
          COGNIO_TOKENS_LAST_EXTENDED_AT,
          new Date().getTime(),
        );
      })
      .catch((e) => {
        logError(ClientErrors.EXTEND_PERSIST_COOKIE_ERROR, e, null);
      });
  }

  /**
   * Checks to see if we logged out from some other tab.
   * @private
   */
  _checkSsoCookies() {
    // reload iFrame to check cookies from the window.postMessage in the cookie.html
    if (this.refreshToken) {
      let iframe = document?.getElementById(COOKIE_IFRAME_ID);
      // contentWindow is null when the iframe has no browsing context (not
      // appended yet, or discarded by a host app re-render). Recreate it so
      // the cookie check keeps polling instead of throwing on `.location`.
      if (!iframe?.contentWindow && document?.body) {
        iframe?.remove();
        this.loadCookieIframe();
        iframe = document.getElementById(COOKIE_IFRAME_ID);
      }
      iframe?.contentWindow?.location.replace(
        getTokenBaseUrlByHostName() + COOKIE_IFRAME_HTML,
      );
      setTimeout(() => {
        this._checkSsoCookies();
      }, POLL_COOKIE_EXISTENCE);
    }
  }

  _getServerSessionBaseUrl() {
    let baseUrl = getURLConfigs().BASE_URL_WWW;
    if (window.location.hostname.indexOf('tinyprints.com') > -1) {
      baseUrl = getURLConfigs().BASE_URL_TP;
    } else if (window.location.hostname.indexOf('share.') > -1) {
      baseUrl = getURLConfigs().BASE_URL_SHARE;
    }
    return baseUrl;
  }

  hasSflyTrk_3_0_Value() {
    return getCookieValue(SFLY3_TRK_COOKIE_NAME) === SFLY3_TRK_COOKIE_VALUE;
  }

  async _recursiveResyncTokens(currentCall) {
    currentCall = currentCall + 1;
    const MAX_ATTEMPTS = 10;
    const wait = function () {
      return new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });
    };
    if (currentCall <= MAX_ATTEMPTS) {
      try {
        const tokens = await this.resyncTokens();
        if (tokens) return { tokens };
      } catch (error) {
        await wait();
        const tokens = await this._recursiveResyncTokens(currentCall);
        if (tokens) return { tokens };
      }
    } else {
      throw new Error(
        `Recursive resync tokens failed after ${MAX_ATTEMPTS} attempts`,
      );
    }
  }

  // Helper function to configure user preference
  async setCookiePreferences(
    userIdToken = undefined,
    currentUserDetails = undefined,
  ) {
    let _idToken = null,
      _currentUser = null;
    if (userIdToken && currentUserDetails) {
      _idToken = userIdToken;
      _currentUser = currentUserDetails;
    } else {
      _idToken = await this.getIdToken();
      _currentUser = this.currentUser;
    }
    const postUrl = await getCookiePreferencesApiCall(
      _currentUser.sfly_uid,
      _idToken.jwtToken,
    );
    await postPreferencesCookiesApiCall(
      _currentUser,
      postUrl,
      _idToken.jwtToken,
    );
    Promise.resolve();
  }
}

//window._sflyAuthClient = new AuthClient();
